import { useEffect, useRef, useState } from "react";

/**
 * Last non-null value. Modals use it so their content does not change
 * during the close animation, after the target state has been cleared.
 */
export function useLastDefined<T>(value: T | null): T | null {
  const ref = useRef<T | null>(value);
  if (value !== null) ref.current = value;
  return ref.current;
}

/** The value after it stopped changing for `ms`. Used for search-as-you-type. */
export function useDebounced<T>(value: T, ms = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** Content width of an element, kept up to date as it resizes. Pass the element from a callback ref. */
export function useElementWidth(el: Element | null): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return width;
}

/** The browser's IANA time zone, e.g. Asia/Jakarta. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}
